/**
 * DP / Folha — Fase 3.2: 13º Salário, Férias e Rescisão
 * Aba do FolhaPagamentoTab — três sub-abas internas.
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { validateDateBR } from '../../lib/helpers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { PercentInput } from '../ui/percent-input';
import Autocomplete from '../ui/autocomplete';
import { useConfirm } from '../../hooks/useConfirm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Gift, Sun, FileX, Plus, Trash2, Pencil, Loader2, Calculator, Receipt, AlertCircle, Download, RefreshCw, Eye,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v) => `${(Number(v) * 100).toFixed(1).replace('.', ',')}%`;
const fmtDate = (s) => s ? s.slice(0, 10).split('-').reverse().join('/') : '—';
const normalizarHorasFolha = (valor) => {
  if (valor === null || valor === undefined) return 0;
  const bruto = Number(String(valor).trim().replace(',', '.'));
  if (!Number.isFinite(bruto)) return 0;
  const sinal = bruto < 0 ? -1 : 1;
  const absoluto = Math.abs(bruto);
  const inteiro = Math.trunc(absoluto);
  const centesimos = Math.round((absoluto - inteiro) * 100);
  if (centesimos >= 60) return Number((sinal * bruto).toFixed(2));
  return Number((sinal * (inteiro + (centesimos / 60))).toFixed(2));
};
const fmtHoras = (valor) => Number(valor || 0).toFixed(2).replace('.', ',');

// ============================================================================
// MEMÓRIA DE CÁLCULO LEGÍVEL — traduz a memória técnica em demonstrativo
// para o analista de folha (base de cálculo, faixas, alíquotas, deduções).
// ============================================================================
const MemoriaLegivel = ({ memoria }) => {
  if (!memoria || typeof memoria !== 'object') return null;

  // INSS — tabela progressiva por faixas
  if (memoria.detalhe_faixas) {
    return (
      <div className="space-y-2">
        <p className="text-slate-600">
          Base de cálculo: <b>{fmtBRL(memoria.base_aplicada ?? memoria.base_recebida)}</b>
          {memoria.teto && Number(memoria.base_recebida) > Number(memoria.teto) && (
            <span className="text-amber-600"> (limitada ao teto de {fmtBRL(memoria.teto)})</span>
          )}
        </p>
        <p className="text-slate-500">O INSS é progressivo: cada parte do salário paga a alíquota da sua faixa.</p>
        <table className="w-full border rounded">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-1.5">Faixa salarial</th>
              <th className="text-right p-1.5">Alíquota</th>
              <th className="text-right p-1.5">Parte do salário na faixa</th>
              <th className="text-right p-1.5">Contribuição</th>
            </tr>
          </thead>
          <tbody>
            {memoria.detalhe_faixas.map((f, i) => (
              <tr key={i} className="border-t">
                <td className="p-1.5">R$ {f.faixa}</td>
                <td className="p-1.5 text-right">{fmtPct(f.aliquota)}</td>
                <td className="p-1.5 text-right">{fmtBRL(f.valor_na_faixa)}</td>
                <td className="p-1.5 text-right font-semibold text-red-700">{fmtBRL(f.contribuicao)}</td>
              </tr>
            ))}
            <tr className="border-t bg-slate-50 font-bold">
              <td colSpan={3} className="p-1.5 text-right">Total do desconto →</td>
              <td className="p-1.5 text-right text-red-700">
                {fmtBRL(memoria.detalhe_faixas.reduce((s, f) => s + (f.contribuicao || 0), 0))}
              </td>
            </tr>
          </tbody>
        </table>
        {memoria.fonte_legal && <p className="text-slate-400 italic">{memoria.fonte_legal}</p>}
      </div>
    );
  }

  if (memoria.tipo === 'salario_base' && memoria.base_marcada !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Base bruta marcada para pensão: <b>{fmtBRL(memoria.base_marcada)}</b></p>
        <p>(-) INSS: <b>{fmtBRL(memoria.inss)}</b></p>
        <p>(-) IRRF: <b>{fmtBRL(memoria.irrf)}</b></p>
        <p className="border-t border-slate-200 pt-1">Base líquida da pensão: <b>{fmtBRL(memoria.base_calculo)}</b></p>
        <p>Percentual aplicado: <b>{fmtPct(Number(memoria.percentual || 0) / 100)}</b></p>
      </div>
    );
  }

  // IRRF — passo a passo da base + tabela progressiva por faixas
  if (memoria.detalhe_faixas_irrf || memoria.faixa || memoria.base_calculo !== undefined) {
    return (
      <div className="space-y-2">
        <p className="text-slate-600">Base original: <b>{fmtBRL(memoria.base_original)}</b></p>
        {memoria.desconto_inss !== undefined && memoria.desconto_inss > 0 && (
          <p className="text-slate-600">(-) INSS: <b>{fmtBRL(memoria.desconto_inss)}</b></p>
        )}
        {memoria.base_liquida !== undefined && (
          <p className="text-slate-600">= Base após INSS: <b>{fmtBRL(memoria.base_liquida)}</b></p>
        )}
        {memoria.deducao_dependentes !== undefined && (
          <p className="text-slate-600">(-) Dedução por dependentes: <b>{fmtBRL(memoria.deducao_dependentes)}</b></p>
        )}
        {memoria.desconto_simplificado !== undefined && (
          <p className="text-slate-600">(-) Desconto simplificado: <b>{fmtBRL(memoria.desconto_simplificado)}</b></p>
        )}
        {memoria.base_calculo !== undefined && <p className="text-slate-600">= Base de cálculo: <b>{fmtBRL(memoria.base_calculo)}</b></p>}

        {memoria.detalhe_faixas_irrf && memoria.detalhe_faixas_irrf.length > 0 && (
          <>
            <p className="text-slate-500 text-sm">O IRRF é progressivo: cada faixa de renda é tributada pela alíquota da sua faixa.</p>
            <table className="w-full border rounded">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-1.5">Faixa de renda</th>
                  <th className="text-right p-1.5">Alíquota</th>
                  <th className="text-right p-1.5">Base na faixa</th>
                  <th className="text-right p-1.5">Imposto</th>
                </tr>
              </thead>
              <tbody>
                {memoria.detalhe_faixas_irrf.map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5">R$ {f.faixa}</td>
                    <td className="p-1.5 text-right">{fmtPct(f.aliquota)}</td>
                    <td className="p-1.5 text-right">{fmtBRL(f.base_na_faixa)}</td>
                    <td className="p-1.5 text-right font-semibold text-red-700">{fmtBRL(f.imposto_na_faixa)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-slate-50 font-bold">
                  <td colSpan={3} className="p-1.5 text-right">Total do imposto →</td>
                  <td className="p-1.5 text-right text-red-700">
                    {fmtBRL(memoria.detalhe_faixas_irrf.reduce((s, f) => s + (f.imposto_na_faixa || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {(!memoria.detalhe_faixas_irrf || memoria.detalhe_faixas_irrf.length === 0) && memoria.faixa && (
          <p className="bg-amber-50 border border-amber-100 rounded p-1.5 mt-1">
            Faixa aplicada: <b>R$ {memoria.faixa.intervalo}</b> → alíquota de <b>{fmtPct(memoria.faixa.aliquota)}</b>,
            menos parcela a deduzir de <b>{fmtBRL(memoria.faixa.deducao_legal)}</b>
          </p>
        )}

        {(memoria.irrf_tradicional !== undefined && memoria.irrf_simplificado !== undefined) && (
          <p className="text-slate-500 mt-1">Comparação: tradicional <b>{fmtBRL(memoria.irrf_tradicional)}</b> vs simplificado <b>{fmtBRL(memoria.irrf_simplificado)}</b> → aplicado o menor{memoria.modo_aplicado ? ` (${memoria.modo_aplicado})` : ""}.</p>
        )}
        {memoria.modo && <p className="text-slate-400 italic">Modo de apuração: {memoria.modo}</p>}
        {memoria.reducao_lei_15270 && (
          <p className="text-blue-600 text-sm">
            {memoria.reducao_lei_15270.regra === 'isencao_total'
              ? `✔ Isento (Lei 15.270/2025 — base ≤ ${fmtBRL(memoria.reducao_lei_15270.limite)})`
              : memoria.reducao_lei_15270.regra === 'reducao_parcial'
              ? `✔ Redução parcial (Lei 15.270/2025): redução de ${fmtBRL(memoria.reducao_lei_15270.reducao_aplicada)}`
              : ''}
          </p>
        )}
      </div>
    );
  }

  if (memoria.adicional && memoria.horas !== undefined && memoria.valor_hora !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Quantidade de horas: <b>{memoria.horas}</b></p>
        <p>Valor-hora: <b>{fmtBRL(memoria.valor_hora)}</b></p>
        <p>Adicional aplicado: <b>{memoria.adicional}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor calculado: <b className="text-emerald-700">{fmtBRL(memoria.valor)}</b></p>
        {memoria.fonte && <p className="text-slate-400 italic">Fonte: {memoria.fonte}</p>}
      </div>
    );
  }

  if ((memoria.dias_repouso !== undefined || memoria.domingos_feriados !== undefined) && memoria.dias_uteis !== undefined && memoria.base !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Base das horas extras: <b>{fmtBRL(memoria.base)}</b></p>
        <p>Dias de repouso, domingos e feriados: <b>{memoria.dias_repouso ?? memoria.domingos_feriados}</b></p>
        <p>Dias úteis: <b>{memoria.dias_uteis}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor calculado do DSR: <b className="text-emerald-700">{fmtBRL(memoria.valor)}</b></p>
        {memoria.ajuste_competencia && <p className="text-slate-400 italic">Dias parametrizados na competência.</p>}
        {memoria.fonte && <p className="text-slate-400 italic">Fonte: {memoria.fonte}</p>}
      </div>
    );
  }

  if (memoria.tipo_beneficio && memoria.dependente_nome) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Dependente: <b>{memoria.dependente_nome}</b></p>
        <p>Benefício: <b>{String(memoria.tipo_beneficio).replaceAll('_', ' ')}</b></p>
        <p>Competência: <b>{String(memoria.competencia || '').replaceAll('_', ' ')}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor descontado: <b className="text-red-700">{fmtBRL(memoria.valor)}</b></p>
      </div>
    );
  }

  // Memórias simples (avos do 13º, fórmulas, ponto, multa FGTS...) — frases legíveis
  const LABELS = {
    avos: 'Meses trabalhados (avos)', salario_base: 'Salário base',
    valor_integral: '13º integral', percentual: 'Percentual aplicado',
    formula: 'Fórmula', dias: 'Dias', saldo_fgts_depositado: 'Saldo FGTS depositado',
    motivo: 'Motivo', aliquota: 'Alíquota', base: 'Base de cálculo',
    horas: 'Horas', valor_hora: 'Valor-hora', adicional: 'Adicional', fonte: 'Fonte',
    composicao_base: 'Composição da base', verbas_isentas: 'Verbas isentas',
    inss_sobre_saldo: 'INSS sobre saldo de salário', inss_sobre_13: 'INSS sobre 13º proporcional',
    irrf_sobre_saldo: 'IRRF sobre saldo de salário', irrf_sobre_13: 'IRRF sobre 13º proporcional',
    saldo_salario: 'Saldo de salário', observacao: 'Observação',
  };
  return (
    <div className="space-y-0.5 text-slate-600">
      {Object.entries(memoria).map(([k, v]) => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'object') {
          return (
            <div key={k} className="mt-1">
              <p className="font-semibold text-slate-700">{LABELS[k] || k.replaceAll('_', ' ')}:</p>
              <div className="pl-2 border-l-2 border-slate-200"><MemoriaLegivel memoria={v} /></div>
            </div>
          );
        }
        const isMoney = typeof v === 'number' && !['avos', 'dias', 'dias_afastados', 'dias_pagos', 'dias_nao_pagos', 'percentual', 'aliquota', 'horas'].includes(k);
        const isPct = ['percentual', 'aliquota'].includes(k) && typeof v === 'number' && v <= 1;
        return (
          <p key={k}>
            {LABELS[k] || k.replaceAll('_', ' ')}: <b>{isMoney ? fmtBRL(v) : isPct ? fmtPct(v) : String(v)}</b>
          </p>
        );
      })}
    </div>
  );
};


// ============================================================================
// COMPONENTE: Lançar Rubrica (reutilizado por Férias e 13º)
// ============================================================================
const LancarRubricaDialog = ({ open, onOpenChange, onConfirm, loading }) => {
  const [rubrica, setRubrica] = useState(null);
  const [valorProvento, setValorProvento] = useState(0);
  const [valorDesconto, setValorDesconto] = useState(0);
  const [referencia, setReferencia] = useState('');

  const handleConfirm = () => {
    if (!rubrica) return toast.error('Selecione uma rubrica');
    const valor = rubrica.tipo === 'desconto' ? valorDesconto : valorProvento;
    if (valor <= 0) return toast.error('Informe um valor válido');
    onConfirm({
      rubrica_id: rubrica.id,
      valor_provento: rubrica.tipo === 'provento' ? valor : 0,
      valor_desconto: rubrica.tipo === 'desconto' ? valor : 0,
      referencia: referencia || null,
    });
  };

  const reset = () => {
    setRubrica(null);
    setValorProvento(0);
    setValorDesconto(0);
    setReferencia('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lançar Rubrica Manual</DialogTitle>
          <DialogDescription>Selecione a rubrica e informe o valor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Rubrica *</Label>
            <Autocomplete
              value={rubrica?.id || ''}
              onValueChange={(v, item) => { setRubrica(item || null); setValorProvento(0); setValorDesconto(0); }}
              endpoint={`${API}/api/calculos-dp/rubricas`}
              searchParam="q"
              getOptionLabel={(item) => `${item.codigo} — ${item.descricao} (${item.tipo})`}
              getOptionValue={(item) => item.id}
              placeholder="Digite código ou nome..."
              extraParams={{ ativo: true }}
            />
          </div>
          <div><Label>{rubrica?.tipo === 'desconto' ? 'Valor do desconto' : 'Valor do provento'}</Label><CurrencyInput value={rubrica?.tipo === 'desconto' ? valorDesconto : valorProvento} onChange={rubrica?.tipo === 'desconto' ? setValorDesconto : setValorProvento} /></div>
          <div><Label>Referência (opcional)</Label><Input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Ex.: 8.0 horas" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lançar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// SUB-TAB: 13º SALÁRIO
// ============================================================================
const Decimo13Tab = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculando13, setRecalculando13] = useState(null);
  const [comps, setComps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [detalhe13Comp, setDetalhe13Comp] = useState(null);
  const [detalhe13Holerite, setDetalhe13Holerite] = useState(null);
  const [memExpand13, setMemExpand13] = useState(null);
  const [lancarRubrica13Open, setLancarRubrica13Open] = useState(false);
  const [lancandoRubrica13, setLancandoRubrica13] = useState(false);
  const [modo, setModo] = useState('coletivo');
  const empty = {
    ano: new Date().getFullYear(),
    parcela: 'primeira',
    data_pagamento: '',
    percentual_1a_parcela: 0.5,
    observacoes: '',
    employee_id: '',
  };
  const [form, setForm] = useState(empty);

  const loadComps = useCallback(async () => {
    try {
      const [r, re] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/competencias`, authHeader()),
        axios.get(`${API}/api/employees`, authHeader()),
      ]);
      const items = (r.data || []).filter(c => c.tipo === '13_primeira' || c.tipo === '13_segunda');
      setComps(items);
      setEmployees((Array.isArray(re.data) ? re.data : re.data.items || []).filter(e => e.situacao === 'ATIVO' || !e.situacao));
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { loadComps(); }, [loadComps]);

  const calcular = async () => {
    if (!form.data_pagamento) return toast.error('Data de pagamento obrigatória');
    if (!validateDateBR(form.data_pagamento, { pastYears: 15, futureYears: 15 })) return toast.error('Data de pagamento inválida');
    if (modo === 'individual' && !form.employee_id) return toast.error('Selecione o colaborador');
    setLoading(true);
    try {
      const payload = { ...form, employee_id: modo === 'individual' ? form.employee_id : null };
      const r = await axios.post(`${API}/api/calculos-dp/decimo-terceiro/calcular`, payload, authHeader());
      toast.success(`✅ ${r.data.totais.holerites_processados} holerites · Líquido total ${fmtBRL(r.data.totais.liquido)}`, { duration: 5000 });
      setOpen(false); setForm(empty); loadComps();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const recalcular13 = async (id) => {
    setRecalculando13(id);
    try {
      await axios.post(`${API}/api/calculos-dp/decimo-terceiro/${id}/recalcular`, {}, authHeader());
      toast.success('13º recalculado com sucesso');
      loadComps();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao recalcular'); }
    finally { setRecalculando13(null); }
  };

  const verDetalhe13 = async (compId) => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/competencias/${compId}/holerites`, authHeader());
      setDetalhe13Comp(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao carregar detalhes'); }
  };

  const refreshDetalhe13Comp = async () => {
    if (!detalhe13Comp?.competencia?.id) return;
    const r = await axios.get(`${API}/api/calculos-dp/competencias/${detalhe13Comp.competencia.id}/holerites`, authHeader());
    setDetalhe13Comp(r.data);
  };

  const verHolerite13 = async (hId) => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/holerites/${hId}`, authHeader());
      setDetalhe13Holerite(r.data.holerite);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao carregar holerite'); }
  };

  const refreshHolerite13 = async () => {
    if (!detalhe13Holerite?.id) return;
    const r = await axios.get(`${API}/api/calculos-dp/holerites/${detalhe13Holerite.id}`, authHeader());
    setDetalhe13Holerite(r.data.holerite);
  };

  const lancarRubrica13 = async (payload) => {
    if (!detalhe13Holerite?.id) return;
    setLancandoRubrica13(true);
    try {
      await axios.post(`${API}/api/calculos-dp/holerites/${detalhe13Holerite.id}/lancar-rubrica`, payload, authHeader());
      toast.success('Rubrica lançada com sucesso');
      setLancarRubrica13Open(false);
      await refreshHolerite13();
      await refreshDetalhe13Comp();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLancandoRubrica13(false); }
  };

  const removerItem13 = async (itemId) => {
    if (!detalhe13Holerite?.id) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/holerites/${detalhe13Holerite.id}/itens/${itemId}`, authHeader());
      toast.success('Item removido');
      await refreshHolerite13();
      await refreshDetalhe13Comp();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-indigo-600" />13º Salário</CardTitle>
          <CardDescription>
            <b>1ª parcela:</b> 50% do 13º proporcional, pago até 30/Nov, SEM INSS/IRRF.<br />
            <b>2ª parcela:</b> 13º integral com INSS/IRRF sobre o total, descontando o que foi pago na 1ª parcela.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setOpen(true)} data-testid="dp-13-novo-btn"><Plus className="h-4 w-4 mr-2" /> Calcular Parcela</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Histórico de Parcelas Calculadas</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left p-2">Ano</th>
                <th className="text-left p-2">Parcela</th>
                <th className="text-left p-2">Pagamento</th>
                <th className="text-right p-2">Holerites</th>
                <th className="text-right p-2">Líquido Total</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {comps.length === 0 ? (
                <tr><td colSpan={7} className="p-4 text-center text-slate-500">Nenhuma parcela calculada.</td></tr>
              ) : comps.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2">{c.ano}</td>
                  <td className="p-2"><Badge variant="outline">{c.tipo === '13_primeira' ? '1ª parcela' : '2ª parcela'}</Badge></td>
                  <td className="p-2">{fmtDate(c.data_pagamento)}</td>
                  <td className="p-2 text-right">{c.totais?.holerites_processados || 0}</td>
                  <td className="p-2 text-right font-semibold text-indigo-700">{fmtBRL(c.totais?.liquido)}</td>
                  <td className="p-2"><Badge>{c.status}</Badge></td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => verDetalhe13(c.id)} title="Ver detalhes">
                      <Eye className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => recalcular13(c.id)} disabled={recalculando13 === c.id} title="Recalcular">
                      {recalculando13 === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-slate-600" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detalhe da competência (lista de holerites) */}
      <Dialog open={!!detalhe13Comp} onOpenChange={(o) => !o && setDetalhe13Comp(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhamento — {detalhe13Comp?.competencia?.tipo === '13_primeira' ? '1ª' : '2ª'} parcela {detalhe13Comp?.competencia?.ano}</DialogTitle>
          </DialogHeader>
          {detalhe13Comp && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase">
                  <tr><th className="text-left p-2">Colaborador</th><th className="text-right p-2">Salário</th><th className="text-right p-2">Proventos</th><th className="text-right p-2">Descontos</th><th className="text-right p-2">Líquido</th><th className="text-right p-2"></th></tr>
                </thead>
                <tbody>
                  {(detalhe13Comp.holerites || []).length === 0 ? (
                    <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nenhum holerite.</td></tr>
                  ) : detalhe13Comp.holerites.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="p-2 font-medium">{h.employee_nome}</td>
                      <td className="p-2 text-right">{fmtBRL(h.salario_base)}</td>
                      <td className="p-2 text-right text-emerald-700">{fmtBRL(h.total_proventos)}</td>
                      <td className="p-2 text-right text-red-700">{fmtBRL(h.total_descontos)}</td>
                      <td className="p-2 text-right font-bold text-indigo-700">{fmtBRL(h.total_liquido)}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => verHolerite13(h.id)} title="Ver itens">
                          <Receipt className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detalhe do holerite de 13º com lançamento de rubricas */}
      <Dialog open={!!detalhe13Holerite} onOpenChange={(o) => { if (!o) { setDetalhe13Holerite(null); setMemExpand13(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Holerite 13º — {detalhe13Holerite?.employee_nome}</DialogTitle>
          </DialogHeader>
          {detalhe13Holerite && (
            <>
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="outline" onClick={() => setLancarRubrica13Open(true)} data-testid="dp-13-lancar-rubrica">
                  <Plus className="h-3 w-3 mr-1" /> Lançar Rubrica
                </Button>
              </div>
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase"><tr><th className="text-left p-2">Cód.</th><th className="text-left p-2">Rubrica</th><th className="text-right p-2">Provento</th><th className="text-right p-2">Desconto</th><th className="text-right p-2"></th></tr></thead>
                <tbody>
                  {detalhe13Holerite.itens.map((it, i) => (
                    <React.Fragment key={it.id || i}>
                      <tr className={`border-t ${it.tipo === 'informativa' ? 'bg-slate-50 text-slate-500' : ''}`}>
                        <td className="p-2 font-mono text-xs">{it.rubrica_codigo}</td>
                        <td className="p-2">{it.rubrica_descricao}</td>
                        <td className="p-2 text-right text-emerald-700">{it.valor_provento > 0 ? fmtBRL(it.valor_provento) : '—'}</td>
                        <td className="p-2 text-right text-red-700">{it.valor_desconto > 0 ? fmtBRL(it.valor_desconto) : '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {it.memoria && (
                            <Button size="sm" variant="ghost" onClick={() => setMemExpand13(memExpand13 === i ? null : i)}
                              title="Ver memória de cálculo" className="h-7 text-[10px] text-indigo-600">
                              <Eye className="h-3.5 w-3.5 mr-1" /> Memória
                            </Button>
                          )}
                          {it.origem === 'manual' && (
                            <Button size="sm" variant="ghost" onClick={() => removerItem13(it.id)} title="Remover item manual">
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {memExpand13 === i && it.memoria && (
                        <tr key={`mem-${i}`} className="bg-slate-50/50">
                          <td colSpan={5} className="p-3 pl-8 border-t border-slate-200">
                            <MemoriaLegivel memoria={it.memoria} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-bold">
                  <tr><td colSpan={4} className="p-2 text-right">LÍQUIDO →</td><td className="p-2 text-right text-xl text-indigo-700">{fmtBRL(detalhe13Holerite.total_liquido)}</td></tr>
                </tfoot>
              </table>

              {detalhe13Holerite.itens.some(it => it.memoria) && (
                <details className="text-xs mt-3">
                  <summary className="cursor-pointer font-semibold text-slate-700">📊 Memória de Cálculo (auditoria)</summary>
                  <div className="mt-2 space-y-2">
                    {detalhe13Holerite.itens.filter(it => it.memoria).map((it, i) => (
                      <Card key={i} className="bg-slate-50">
                        <CardHeader className="pb-1"><CardTitle className="text-xs">{it.rubrica_codigo} — {it.rubrica_descricao}</CardTitle></CardHeader>
                        <CardContent>
                          <MemoriaLegivel memoria={it.memoria} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <LancarRubricaDialog
        open={lancarRubrica13Open}
        onOpenChange={setLancarRubrica13Open}
        onConfirm={lancarRubrica13}
        loading={lancandoRubrica13}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dp-13-dialog">
          <DialogHeader>
            <DialogTitle>Calcular 13º Salário</DialogTitle>
            <DialogDescription>Será criada uma competência específica e calculados os holerites de todos os colaboradores ATIVOS.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ano *</Label><Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" /></div>
            <div>
              <Label>Parcela *</Label>
              <Select value={form.parcela} onValueChange={(v) => setForm({ ...form, parcela: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primeira">1ª parcela (até 30/Nov)</SelectItem>
                  <SelectItem value="segunda">2ª parcela (até 20/Dez)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Data de Pagamento *</Label><Input type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value.slice(0, 10) })} /></div>
            {form.parcela === 'primeira' && (
              <div className="col-span-2">
                <Label>Percentual da 1ª parcela</Label>
                <PercentInput
                  value={form.percentual_1a_parcela * 100}
                  onChange={v => setForm({ ...form, percentual_1a_parcela: v / 100 })}
                  format="###,##"
                  max={100}
                />
              </div>
            )}
            <div className="col-span-2"><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={calcular} disabled={loading} data-testid="dp-13-calcular-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calcular'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: FÉRIAS
// ============================================================================
const FeriasTab = () => {
  const { confirm: confirmFerias, ConfirmDialog: ConfirmDialogFerias } = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculandoFerias, setRecalculandoFerias] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [memExpandFerias, setMemExpandFerias] = useState(null);
  const [lancarRubricaFeriasOpen, setLancarRubricaFeriasOpen] = useState(false);
  const [lancandoRubricaFerias, setLancandoRubricaFerias] = useState(false);
  const empty = {
    employee_id: '', data_inicio_gozo: '', dias_gozados: 30,
    abono_pecuniario_dias: 0, faltas_injustificadas: 0, adiantar_13_primeira: false,
    periodo_aquisitivo_inicio: '', periodo_aquisitivo_fim: '', observacoes: '',
  };
  const diasDireito = (f) => { f = parseInt(f) || 0; if (f <= 5) return 30; if (f <= 14) return 24; if (f <= 23) return 18; if (f <= 32) return 12; return 0; };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/ferias`, authHeader());
      setItems(r.data || []);
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const calcular = async () => {
    if (!form.employee_id || !form.data_inicio_gozo) return toast.error('Selecione colaborador e data');
    if (!validateDateBR(form.data_inicio_gozo, { pastYears: 15, futureYears: 15 })) return toast.error('Data de início do gozo inválida');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/ferias/calcular`, form, authHeader());
      toast.success(`✅ Férias calculadas: Líquido ${fmtBRL(r.data.total_liquido)}`);
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!(await confirmFerias({ title: 'Excluir cálculo?', description: 'Tem certeza que deseja excluir este cálculo de férias?', confirmLabel: 'Excluir', destructive: true }))) return;
    await axios.delete(`${API}/api/calculos-dp/ferias/${id}`, authHeader());
    toast.success('Excluído'); load();
  };

  const ver = async (id) => {
    const r = await axios.get(`${API}/api/calculos-dp/ferias/${id}`, authHeader());
    setDetalhe(r.data);
  };

  const refreshDetalheFerias = async () => {
    if (!detalhe?.id) return;
    const r = await axios.get(`${API}/api/calculos-dp/ferias/${detalhe.id}`, authHeader());
    setDetalhe(r.data);
  };

  const lancarRubricaFerias = async (payload) => {
    setLancandoRubricaFerias(true);
    try {
      await axios.post(`${API}/api/calculos-dp/ferias/${detalhe.id}/lancar-rubrica`, payload, authHeader());
      toast.success('Rubrica lançada com sucesso');
      setLancarRubricaFeriasOpen(false);
      await refreshDetalheFerias();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLancandoRubricaFerias(false); }
  };

  const removerItemFerias = async (itemId) => {
    try {
      await axios.delete(`${API}/api/calculos-dp/ferias/${detalhe.id}/itens/${itemId}`, authHeader());
      toast.success('Item removido');
      await refreshDetalheFerias();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const recalcularFerias = async (id) => {
    setRecalculandoFerias(id);
    try {
      await axios.post(`${API}/api/calculos-dp/ferias/${id}/recalcular`, {}, authHeader());
      toast.success('Férias recalculadas com sucesso');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao recalcular'); }
    finally { setRecalculandoFerias(null); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sun className="h-5 w-5 text-amber-600" />Cálculo de Férias</CardTitle>
          <CardDescription>30 dias + 1/3 constitucional. Abono pecuniário (até 10 dias) não sofre INSS/IRRF.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setOpen(true)} data-testid="dp-fer-novo-btn"><Plus className="h-4 w-4 mr-2" /> Novo Cálculo</Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-2">Colaborador</th>
              <th className="text-left p-2">Início Gozo</th>
              <th className="text-right p-2">Dias</th>
              <th className="text-right p-2">Abono</th>
              <th className="text-right p-2">Proventos</th>
              <th className="text-right p-2">Descontos</th>
              <th className="text-right p-2">Líquido</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-slate-500">Sem cálculos.</td></tr>
            ) : items.map((f) => (
              <tr key={f.id} className="border-t hover:bg-slate-50">
                <td className="p-2"><div className="font-medium">{f.employee_nome}</div><div className="text-xs text-slate-500">{f.employee_cargo}</div></td>
                <td className="p-2">{fmtDate(f.data_inicio_gozo)}</td>
                <td className="p-2 text-right">{f.dias_gozados}</td>
                <td className="p-2 text-right">{f.abono_pecuniario_dias || 0}</td>
                <td className="p-2 text-right text-emerald-700">{fmtBRL(f.total_proventos)}</td>
                <td className="p-2 text-right text-red-700">{fmtBRL(f.total_descontos)}</td>
                <td className="p-2 text-right font-bold text-indigo-700">{fmtBRL(f.total_liquido)}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => ver(f.id)}><Receipt className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => recalcularFerias(f.id)} disabled={recalculandoFerias === f.id} title="Recalcular">
                    {recalculandoFerias === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-slate-600" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(f.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="dp-fer-dialog">
          <DialogHeader>
            <DialogTitle>Cálculo de Férias</DialogTitle>
            <DialogDescription>Define dias gozados, abono pecuniário e período aquisitivo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Autocomplete
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cpf ? ` · ${item.cpf}` : ''}${item.cargo ? ` · ${item.cargo}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o nome, CPF ou cargo..."
                extraParams={{ active_only: true, regime: 'CLT' }}
                emptyMessage="Nenhum funcionário CLT encontrado"
              />
            </div>
            <div><Label>Início do Gozo *</Label><Input type="date" value={form.data_inicio_gozo} onChange={(e) => setForm({ ...form, data_inicio_gozo: e.target.value })} /></div>
            <div>
              <Label>Faltas injustificadas (período aquisitivo)</Label>
              <Input type="number" min="0" value={form.faltas_injustificadas} onChange={(e) => setForm({ ...form, faltas_injustificadas: parseInt(e.target.value) || 0 })} />
              <p className="text-xs text-slate-500 mt-1">{diasDireito(form.faltas_injustificadas) === 0 ? 'Mais de 32 faltas: perde o direito a férias (CLT art. 130).' : `Direito a ${diasDireito(form.faltas_injustificadas)} dias de férias (CLT art. 130).`}</p>
            </div>
            <div><Label>Dias Gozados (5-{diasDireito(form.faltas_injustificadas) || 30}) *</Label><Input type="number" min="5" max={diasDireito(form.faltas_injustificadas) || 30} value={form.dias_gozados} onChange={(e) => setForm({ ...form, dias_gozados: parseInt(e.target.value) || 30 })} /></div>
            <div><Label>Abono Pecuniário (0-{Math.floor((diasDireito(form.faltas_injustificadas) || 30) / 3)})</Label><Input type="number" min="0" max={Math.floor((diasDireito(form.faltas_injustificadas) || 30) / 3)} value={form.abono_pecuniario_dias} onChange={(e) => setForm({ ...form, abono_pecuniario_dias: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex items-center gap-2 mt-6">
              <Switch checked={form.adiantar_13_primeira} onCheckedChange={(v) => setForm({ ...form, adiantar_13_primeira: v })} />
              <Label>Adiantar 1ª parcela 13º</Label>
            </div>
            <div><Label>Per. Aquisitivo Início</Label><Input type="date" value={form.periodo_aquisitivo_inicio} onChange={(e) => setForm({ ...form, periodo_aquisitivo_inicio: e.target.value })} /></div>
            <div><Label>Per. Aquisitivo Fim</Label><Input type="date" value={form.periodo_aquisitivo_fim} onChange={(e) => setForm({ ...form, periodo_aquisitivo_fim: e.target.value })} /></div>
            <div className="col-span-2"><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={calcular} disabled={loading || diasDireito(form.faltas_injustificadas) === 0} data-testid="dp-fer-calcular-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calcular'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detalhe} onOpenChange={(o) => { if (!o) { setDetalhe(null); setMemExpandFerias(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhamento — {detalhe?.employee_nome}</DialogTitle>
          </DialogHeader>
{detalhe && (
            <>
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="outline" onClick={() => setLancarRubricaFeriasOpen(true)} data-testid="dp-fer-lancar-rubrica">
                  <Plus className="h-3 w-3 mr-1" /> Lançar Rubrica
                </Button>
              </div>
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase"><tr><th className="text-left p-2">Cód.</th><th className="text-left p-2">Rubrica</th><th className="text-right p-2">Provento</th><th className="text-right p-2">Desconto</th><th className="text-right p-2"></th></tr></thead>
                <tbody>
                  {detalhe.itens.map((it, i) => (
                    <React.Fragment key={it.id || i}>
                      <tr className={`border-t ${it.tipo === 'informativa' ? 'bg-slate-50 text-slate-500' : ''}`}>
                        <td className="p-2 font-mono text-xs">{it.rubrica_codigo}</td>
                        <td className="p-2">{it.rubrica_descricao}</td>
                        <td className="p-2 text-right text-emerald-700">{it.valor_provento > 0 ? fmtBRL(it.valor_provento) : '—'}</td>
                        <td className="p-2 text-right text-red-700">{it.valor_desconto > 0 ? fmtBRL(it.valor_desconto) : '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {it.memoria && (
                            <Button size="sm" variant="ghost" onClick={() => setMemExpandFerias(memExpandFerias === i ? null : i)}
                              title="Ver memória de cálculo" className="h-7 text-[10px] text-indigo-600">
                              <Eye className="h-3.5 w-3.5 mr-1" /> Memória
                            </Button>
                          )}
                          {it.origem === 'manual' && (
                            <Button size="sm" variant="ghost" onClick={() => removerItemFerias(it.id)} title="Remover item manual">
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {memExpandFerias === i && it.memoria && (
                        <tr key={`mem-${i}`} className="bg-slate-50/50">
                          <td colSpan={5} className="p-3 pl-8 border-t border-slate-200">
                            <MemoriaLegivel memoria={it.memoria} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-bold">
                  <tr><td colSpan={3} className="p-2 text-right">LÍQUIDO →</td><td className="p-2 text-right text-xl text-indigo-700">{fmtBRL(detalhe.total_liquido)}</td><td /></tr>
                </tfoot>
              </table>

              {/* Memória de cálculo */}
              {detalhe.itens.some(it => it.memoria) && (
                <details className="text-xs mt-3">
                  <summary className="cursor-pointer font-semibold text-slate-700">📊 Memória de Cálculo (auditoria)</summary>
                  <div className="mt-2 space-y-2">
                    {detalhe.itens.filter(it => it.memoria).map((it, i) => (
                      <Card key={i} className="bg-slate-50">
                        <CardHeader className="pb-1"><CardTitle className="text-xs">{it.rubrica_codigo} — {it.rubrica_descricao}</CardTitle></CardHeader>
                        <CardContent>
                          <MemoriaLegivel memoria={it.memoria} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <LancarRubricaDialog
        open={lancarRubricaFeriasOpen}
        onOpenChange={setLancarRubricaFeriasOpen}
        onConfirm={lancarRubricaFerias}
        loading={lancandoRubricaFerias}
      />
      <ConfirmDialogFerias />
    </div>
  );
};

// ============================================================================
// SUB-TAB: RESCISÃO
// ============================================================================
const RescisaoTab = () => {
  const { confirm: confirmRescisao, ConfirmDialog: ConfirmDialogRescisao } = useConfirm();
  const [items, setItems] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [rubricasHoraExtra, setRubricasHoraExtra] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculandoRescisao, setRecalculandoRescisao] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [memExpandResc, setMemExpandResc] = useState(null);
  const empty = {
    employee_id: '', data_rescisao: '', motivo: 'sem_justa_causa',
    data_inicio_aviso: '', aviso_previo: 'indenizado', saldo_fgts_depositado: 0,
    observacoes: '', itens_manuais: [],
  };
  const [form, setForm] = useState(empty);
  const emptyEvento = { descricao: '', tipo: 'provento', valor: 0, modo_calculo: 'valor', rubrica_id: '', horas: '' };
  const [eventoRescisao, setEventoRescisao] = useState(emptyEvento);
  const [editingEventoRescisao, setEditingEventoRescisao] = useState(null);
  const [salvandoEventoRescisao, setSalvandoEventoRescisao] = useState(false);
  const motivoTerminoContrato = form.motivo === 'termino_contrato';
  const usaAvisoTrabalhado = form.aviso_previo === 'trabalhado' && !motivoTerminoContrato;
  const dataPrincipalRescisao = usaAvisoTrabalhado ? form.data_inicio_aviso : form.data_rescisao;

  const addItemManual = () => setForm((f) => ({
    ...f,
    itens_manuais: [
      ...(f.itens_manuais || []),
      { descricao: '', tipo: 'provento', valor: 0, modo_calculo: 'valor', horas: '' },
    ],
  }));
  const updItemManual = (i, patch) => setForm((f) => ({
    ...f,
    itens_manuais: f.itens_manuais.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, ...patch };
      if (patch.tipo && patch.tipo !== 'desconto' && next.modo_calculo === 'horas_desconto_banco') {
        next.modo_calculo = 'valor';
        next.horas = '';
      }
      if (patch.tipo === 'desconto' && next.modo_calculo?.startsWith('hora_extra_')) {
        next.modo_calculo = 'valor';
        next.horas = '';
      }
      if (patch.modo_calculo === 'horas_desconto_banco') {
        next.tipo = 'desconto';
        next.valor = 0;
        if (!next.descricao.trim()) next.descricao = 'Desconto banco de horas';
      }
      if (patch.modo_calculo?.startsWith('hora_extra_')) {
        next.tipo = 'provento';
        next.valor = 0;
        const percentual = patch.modo_calculo.replace('hora_extra_', '').replace('_', '.');
        if (!next.descricao.trim()) next.descricao = `Hora Extra ${percentual}% Rescisão`;
      }
      if (patch.modo_calculo === 'valor') {
        next.horas = '';
      }
      return next;
    }),
  }));
  const delItemManual = (i) => setForm((f) => ({ ...f, itens_manuais: f.itens_manuais.filter((_, idx) => idx !== i) }));

  const load = useCallback(async () => {
    try {
      const [r1, r3, r4] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/rescisao`, authHeader()),
        axios.get(`${API}/api/calculos-dp/rescisao/motivos`, authHeader()),
        axios.get(`${API}/api/calculos-dp/rubricas?ativo=true`, authHeader()),
      ]);
      setItems(r1.data || []);
      // normaliza para {codigo, descricao}
      setMotivos((r3.data || []).map(m => ({ codigo: m.codigo, descricao: m.descricao })));
      setRubricasHoraExtra((r4.data || []).filter((r) => r.tipo === 'provento' && /^hora_extra_\d+(?:_\d+)?$/.test(r.formula_tipo || '')));
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const calcular = async () => {
    if (!form.employee_id || !dataPrincipalRescisao) {
      return toast.error(usaAvisoTrabalhado ? 'Colaborador e início do aviso obrigatórios' : 'Colaborador e data de rescisão obrigatórios');
    }
    if (!validateDateBR(dataPrincipalRescisao, { futureYears: 0, pastYears: 15 })) {
      return toast.error(usaAvisoTrabalhado ? 'Data de início do aviso inválida' : 'Data de rescisão inválida');
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        aviso_previo: motivoTerminoContrato ? 'dispensado' : form.aviso_previo,
        data_rescisao: usaAvisoTrabalhado ? dataPrincipalRescisao : form.data_rescisao,
        data_inicio_aviso: usaAvisoTrabalhado && !motivoTerminoContrato ? dataPrincipalRescisao : '',
      };
      const r = await axios.post(`${API}/api/calculos-dp/rescisao/calcular`, payload, authHeader());
      toast.success(`✅ Rescisão calculada · Líquido ${fmtBRL(r.data.total_liquido)}`, { duration: 5000 });
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!(await confirmRescisao({ title: 'Excluir rescisão?', description: 'Tem certeza que deseja excluir esta rescisão?', confirmLabel: 'Excluir', destructive: true }))) return;
    await axios.delete(`${API}/api/calculos-dp/rescisao/${id}`, authHeader());
    toast.success('Excluída'); load();
  };

  const ver = async (id) => {
    const r = await axios.get(`${API}/api/calculos-dp/rescisao/${id}`, authHeader());
    setDetalhe(r.data);
  };

  const gerarPdf = async (id) => {
    try {
      const res = await axios.post(`${API}/api/calculos-dp/rescisao/${id}/gerar-pdf`, {}, {
        ...authHeader(), responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('PDF gerado com sucesso');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar PDF');
    }
  };

  const recalcularRescisao = async (id) => {
    setRecalculandoRescisao(id);
    try {
      await axios.post(`${API}/api/calculos-dp/rescisao/${id}/recalcular`, {}, authHeader());
      toast.success('Rescisão recalculada com sucesso');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao recalcular'); }
    finally { setRecalculandoRescisao(null); }
  };

  const lancarEventoRescisao = async () => {
    if (!detalhe?.id) return;
    if (!eventoRescisao.descricao?.trim()) return toast.error('Informe a descrição do evento');
    if (eventoRescisao.modo_calculo === 'valor' && !Number(eventoRescisao.valor || 0)) return toast.error('Informe o valor do evento');
    if (eventoRescisao.modo_calculo !== 'valor' && !eventoRescisao.horas) return toast.error('Informe as horas do evento');
    setSalvandoEventoRescisao(true);
    try {
      const r = editingEventoRescisao
        ? await axios.put(`${API}/api/calculos-dp/rescisao/${detalhe.id}/itens/${editingEventoRescisao.id}`, eventoRescisao, authHeader())
        : await axios.post(`${API}/api/calculos-dp/rescisao/${detalhe.id}/lancar-evento`, eventoRescisao, authHeader());
      setDetalhe(r.data);
      setEventoRescisao(emptyEvento);
      setEditingEventoRescisao(null);
      toast.success(editingEventoRescisao ? 'Evento atualizado na rescisão' : 'Evento lançado na rescisão');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao lançar evento');
    } finally {
      setSalvandoEventoRescisao(false);
    }
  };

  const editarEventoRescisao = (item) => {
    const memoria = item.memoria || {};
    setEditingEventoRescisao(item);
    setEventoRescisao({
      descricao: item.rubrica_descricao || '',
      tipo: item.tipo === 'desconto' ? 'desconto' : 'provento',
      valor: item.tipo === 'desconto' ? item.valor_desconto : item.valor_provento,
      rubrica_id: memoria.rubrica_id || '',
      modo_calculo: memoria.fonte === 'banco_horas_manual_desconto' ? 'horas_desconto_banco' : (memoria.tipo || 'valor'),
      horas: memoria.horas_informadas || memoria.horas || '',
    });
  };

  const removerEventoRescisao = async (itemId) => {
    if (!detalhe?.id || !itemId) return;
    try {
      const r = await axios.delete(`${API}/api/calculos-dp/rescisao/${detalhe.id}/itens/${itemId}`, authHeader());
      setDetalhe(r.data?.rescisao || null);
      toast.success('Evento removido da rescisão');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao remover evento');
    }
  };

  const updEventoRescisao = (patch) => {
    setEventoRescisao((atual) => {
      const next = { ...atual, ...patch };
      if (patch.tipo && patch.tipo !== 'desconto' && next.modo_calculo === 'horas_desconto_banco') {
        next.modo_calculo = 'valor';
        next.horas = '';
      }
      if (patch.tipo === 'desconto' && next.modo_calculo?.startsWith('hora_extra_')) {
        next.modo_calculo = 'valor';
        next.horas = '';
      }
      if (patch.modo_calculo === 'horas_desconto_banco') {
        next.tipo = 'desconto';
        next.valor = 0;
        if (!next.descricao.trim()) next.descricao = 'Desconto banco de horas';
      }
      if (patch.modo_calculo?.startsWith('hora_extra_')) {
        next.tipo = 'provento';
        next.valor = 0;
        const percentual = patch.modo_calculo.replace('hora_extra_', '').replace('_', '.');
        if (!next.descricao.trim()) next.descricao = `Hora Extra ${percentual}% Rescisão`;
      }
      if (patch.modo_calculo === 'valor') {
        next.horas = '';
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileX className="h-5 w-5 text-red-600" />Termo de Rescisão (TRCT)</CardTitle>
          <CardDescription>Calcula saldo de salário, aviso prévio (Lei 12.506/11), 13º proporcional, férias proporcionais/vencidas + 1/3 e multa FGTS (40% s/ justa causa; 20% acordo).</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setOpen(true)} data-testid="dp-resc-novo-btn"><Plus className="h-4 w-4 mr-2" /> Nova Rescisão</Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-2">Colaborador</th>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Motivo</th>
              <th className="text-right p-2">Aviso (dias)</th>
              <th className="text-right p-2">Multa FGTS</th>
              <th className="text-right p-2">Líquido</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500">Sem rescisões.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50">
                <td className="p-2"><div className="font-medium">{r.employee_nome}</div><div className="text-xs text-slate-500">{r.employee_cargo}</div></td>
                <td className="p-2">{fmtDate(r.data_rescisao)}</td>
                <td className="p-2 text-xs">{r.motivo_descricao}</td>
                <td className="p-2 text-right">{r.dias_aviso}</td>
                <td className="p-2 text-right">{fmtBRL(r.multa_fgts)}</td>
                <td className="p-2 text-right font-bold text-indigo-700">{fmtBRL(r.total_liquido)}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => gerarPdf(r.id)} title="Gerar PDF do TRCT"><Download className="h-4 w-4 text-blue-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => ver(r.id)}><Receipt className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => recalcularRescisao(r.id)} disabled={recalculandoRescisao === r.id} title="Recalcular">
                    {recalculandoRescisao === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-slate-600" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="dp-resc-dialog">
          <DialogHeader>
            <DialogTitle>Calcular Rescisão (TRCT)</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Autocomplete
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cpf ? ` · ${item.cpf}` : ''}${item.cargo ? ` · ${item.cargo}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o nome, CPF ou cargo..."
                extraParams={{ regime: 'CLT' }}
                emptyMessage="Nenhum funcionário CLT encontrado"
              />
            </div>
            <div>
              <Label>{usaAvisoTrabalhado ? 'Início do Aviso Trabalhado *' : 'Data Rescisão *'}</Label>
              <Input
                type="date"
                value={dataPrincipalRescisao}
                onChange={(e) => setForm((prev) => (
                  usaAvisoTrabalhado
                    ? { ...prev, data_inicio_aviso: e.target.value }
                    : { ...prev, data_rescisao: e.target.value }
                ))}
              />
              {usaAvisoTrabalhado && (
                <p className="mt-1 text-[11px] text-slate-500">
                  O sistema usa esta data como início do aviso e calcula a data final do desligamento pelo término do aviso.
                </p>
              )}
            </div>
            <div>
              <Label>Motivo *</Label>
              <Select value={form.motivo} onValueChange={(v) => setForm((prev) => ({
                ...prev,
                motivo: v,
                aviso_previo: v === 'termino_contrato' ? 'dispensado' : (prev.aviso_previo === 'dispensado' && prev.motivo === 'termino_contrato' ? 'indenizado' : prev.aviso_previo),
                data_inicio_aviso: v === 'termino_contrato' ? '' : prev.data_inicio_aviso,
              }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {motivos.map((m) => (<SelectItem key={m.codigo} value={m.codigo}>{m.descricao}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aviso Prévio</Label>
              <Select
                value={motivoTerminoContrato ? 'dispensado' : form.aviso_previo}
                disabled={motivoTerminoContrato}
                onValueChange={(v) => setForm((prev) => ({
                  ...prev,
                  aviso_previo: v,
                  data_inicio_aviso: v === 'trabalhado' ? (prev.data_inicio_aviso || prev.data_rescisao) : '',
                  data_rescisao: v === 'trabalhado' ? prev.data_rescisao : (prev.data_rescisao || prev.data_inicio_aviso),
                }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="indenizado">Indenizado</SelectItem>
                  <SelectItem value="trabalhado">Trabalhado</SelectItem>
                  <SelectItem value="dispensado">Dispensado</SelectItem>
                </SelectContent>
              </Select>
              {motivoTerminoContrato && (
                <p className="mt-1 text-[11px] text-slate-500">
                  No término normal de contrato não há aviso prévio; se houver encerramento antecipado, o sistema calcula apenas a indenização de 50% dos dias restantes da experiência.
                </p>
              )}
            </div>
            <div><Label>Saldo FGTS Depositado</Label><CurrencyInput value={form.saldo_fgts_depositado} onChange={(v) => setForm({ ...form, saldo_fgts_depositado: v })} /></div>
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Lançamentos manuais</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItemManual} data-testid="dp-resc-add-item"><Plus className="h-3 w-3 mr-1" />Adicionar</Button>
              </div>
              {(form.itens_manuais || []).length === 0 ? (
                <div className="text-sm text-slate-500 border rounded-md px-3 py-2">Nenhum lançamento manual.</div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="hidden md:grid grid-cols-12 gap-2 bg-slate-100 px-3 py-2 text-xs font-medium uppercase text-slate-600">
                    <div className="col-span-4">Descrição</div>
                    <div className="col-span-2">Tipo</div>
                    <div className="col-span-2">Cálculo</div>
                    <div className="col-span-3 text-right">Valor/Horas</div>
                    <div className="col-span-1 text-right">Ações</div>
                  </div>
                  {form.itens_manuais.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end border-t first:border-t-0 md:first:border-t px-3 py-3" data-testid={`dp-resc-item-${i}`}>
                      <div className="col-span-12 md:col-span-4">
                        <Label className="text-xs">Descrição</Label>
                        <Input
                          value={it.descricao}
                          placeholder={it.modo_calculo === 'horas_desconto_banco' ? 'Ex.: Desconto banco de horas' : 'Ex.: Reembolso VR'}
                          onChange={(e) => updItemManual(i, { descricao: e.target.value })}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={it.tipo} onValueChange={(v) => updItemManual(i, { tipo: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="provento">Provento</SelectItem>
                            <SelectItem value="desconto">Desconto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-xs">Cálculo</Label>
                        <Select value={it.modo_calculo || 'valor'} onValueChange={(v) => updItemManual(i, { modo_calculo: v, rubrica_id: rubricasHoraExtra.find((r) => r.formula_tipo === v)?.id || '' })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="valor">Valor informado</SelectItem>
                            <SelectItem value="horas_desconto_banco">Horas negativas de banco</SelectItem>
                            {rubricasHoraExtra.map((rubrica) => (
                              <SelectItem key={rubrica.id} value={rubrica.formula_tipo}>
                                {rubrica.descricao} ({rubrica.formula_tipo.replace('hora_extra_', '').replace('_', '.')}%)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-10 md:col-span-3">
                        {it.modo_calculo !== 'valor' ? (
                          <>
                            <Label className="text-xs">{it.modo_calculo === 'horas_desconto_banco' ? 'Horas' : 'Horas extras'}</Label>
                            <Input
                              value={it.horas || ''}
                              placeholder={it.modo_calculo === 'horas_desconto_banco' ? 'Ex.: 17,39' : 'Ex.: 02,30'}
                              onChange={(e) => updItemManual(i, { horas: e.target.value })}
                            />
                          </>
                        ) : (
                          <>
                            <Label className="text-xs">Valor</Label>
                            <CurrencyInput value={it.valor} onChange={(v) => updItemManual(i, { valor: v })} />
                          </>
                        )}
                      </div>
                      <div className="col-span-2 md:col-span-1 text-right">
                        <Button type="button" size="sm" variant="ghost" onClick={() => delItemManual(i)} title="Remover lançamento"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                      {it.tipo === 'desconto' && it.modo_calculo === 'horas_desconto_banco' && (
                        <div className="col-span-12 text-[11px] text-slate-500 px-1">
                          Horas informadas: <b>{it.horas || '0'}</b> → centesimal: <b>{fmtHoras(normalizarHorasFolha(it.horas || 0))}</b>.
                        </div>
                      )}
                      {it.modo_calculo?.startsWith('hora_extra_') && (
                        <div className="col-span-12 text-[11px] text-slate-500 px-1">
                          Hora extra {it.modo_calculo.replace('hora_extra_', '').replace('_', '.')}% lançada por quantidade de horas.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2"><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
            <div className="col-span-2 text-xs text-slate-500 bg-slate-50 p-2 rounded border">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              O sistema aplica Lei 12.506/11 (aviso proporcional ao tempo de serviço, máx 90 dias) e Lei 13.467/17 (acordo art. 484-A: 50% aviso indenizado e 20% multa FGTS).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={calcular} disabled={loading} data-testid="dp-resc-calcular-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calcular'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detalhe} onOpenChange={(o) => { if (!o) { setDetalhe(null); setMemExpandResc(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>TRCT — {detalhe?.employee_nome}</DialogTitle>
            <DialogDescription>{detalhe?.motivo_descricao} · {fmtDate(detalhe?.data_admissao)} → {fmtDate(detalhe?.data_rescisao)}</DialogDescription>
          </DialogHeader>
          {detalhe && (
            <>
              {detalhe.aviso_previo === 'trabalhado' && detalhe.data_aviso_previo && (
                <div className="text-xs text-slate-600 bg-slate-50 border rounded px-3 py-2">
                  Aviso trabalhado: início em <b>{fmtDate(detalhe.data_aviso_previo)}</b> e desligamento efetivo em <b>{fmtDate(detalhe.data_rescisao)}</b>.
                </div>
              )}
              {detalhe.motivo === 'termino_contrato' && detalhe.indenizacao_termino_antecipado > 0 && (
                <div className="text-xs text-slate-600 bg-slate-50 border rounded px-3 py-2">
                  Término antecipado identificado: previsão contratual em <b>{fmtDate(detalhe.data_termino_contrato_prevista)}</b>, com indenização de 50% sobre <b>{detalhe.dias_indenizacao_termino_antecipado}</b> dias restantes.
                </div>
              )}
              <div className="border rounded-md overflow-hidden">
                <div className="flex items-center justify-between gap-2 bg-slate-100 px-3 py-2">
                  <Label className="text-sm font-medium">{editingEventoRescisao ? 'Editar lançamento manual' : 'Novo lançamento'}</Label>
                  <Badge variant="outline">manual</Badge>
                </div>
                <div className="grid grid-cols-12 gap-2 items-end px-3 py-3">
                  <div className="col-span-12 lg:col-span-4">
                    <Label className="text-xs">Descrição</Label>
                    <Input
                      value={eventoRescisao.descricao}
                      placeholder={eventoRescisao.modo_calculo === 'horas_desconto_banco' ? 'Ex.: Desconto banco de horas' : 'Ex.: Ajuste rescisão'}
                      onChange={(e) => updEventoRescisao({ descricao: e.target.value })}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-2">
                    <Label className="text-xs">Tipo</Label>
                    <Select value={eventoRescisao.tipo} onValueChange={(v) => updEventoRescisao({ tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="provento">Provento</SelectItem>
                        <SelectItem value="desconto">Desconto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 lg:col-span-2">
                    <Label className="text-xs">Cálculo</Label>
                    <Select value={eventoRescisao.modo_calculo || 'valor'} onValueChange={(v) => updEventoRescisao({ modo_calculo: v, rubrica_id: rubricasHoraExtra.find((r) => r.formula_tipo === v)?.id || '' })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="valor">Valor informado</SelectItem>
                        <SelectItem value="horas_desconto_banco">Horas negativas de banco</SelectItem>
                        {rubricasHoraExtra.map((rubrica) => (
                          <SelectItem key={rubrica.id} value={rubrica.formula_tipo}>
                            {rubrica.descricao} ({rubrica.formula_tipo.replace('hora_extra_', '').replace('_', '.')}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-8 lg:col-span-3">
                    {eventoRescisao.modo_calculo !== 'valor' ? (
                      <>
                        <Label className="text-xs">{eventoRescisao.modo_calculo === 'horas_desconto_banco' ? 'Horas' : 'Horas extras'}</Label>
                        <Input
                          value={eventoRescisao.horas || ''}
                          placeholder={eventoRescisao.modo_calculo === 'horas_desconto_banco' ? 'Ex.: 17,39' : 'Ex.: 02,30'}
                          onChange={(e) => updEventoRescisao({ horas: e.target.value })}
                        />
                      </>
                    ) : (
                      <>
                        <Label className="text-xs">Valor</Label>
                        <CurrencyInput value={eventoRescisao.valor} onChange={(v) => updEventoRescisao({ valor: v })} />
                      </>
                    )}
                  </div>
                  <div className="col-span-4 lg:col-span-1">
                    <Button type="button" className="w-full" onClick={lancarEventoRescisao} disabled={salvandoEventoRescisao} title={editingEventoRescisao ? 'Salvar alteração' : 'Lançar evento'}>
                      {salvandoEventoRescisao ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                      <span className="hidden lg:inline">{editingEventoRescisao ? 'Salvar' : 'Lançar'}</span>
                    </Button>
                    {editingEventoRescisao && <Button type="button" variant="outline" onClick={() => { setEditingEventoRescisao(null); setEventoRescisao(emptyEvento); }}>Cancelar</Button>}
                  </div>
                </div>
              </div>
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase"><tr><th className="text-left p-2">Cód.</th><th className="text-left p-2">Rubrica</th><th className="text-right p-2">Provento</th><th className="text-right p-2">Desconto</th><th className="text-right p-2">Ações</th></tr></thead>
                <tbody>
                  {detalhe.itens.map((it, i) => (
                    <React.Fragment key={i}>
                      <tr className={`border-t ${it.tipo === 'informativa' ? 'bg-slate-50 text-slate-500' : ''}`}>
                        <td className="p-2 font-mono text-xs">{it.rubrica_codigo}</td>
                        <td className="p-2">{it.rubrica_descricao}</td>
                        <td className="p-2 text-right text-emerald-700">{it.valor_provento > 0 ? fmtBRL(it.valor_provento) : '—'}</td>
                        <td className="p-2 text-right text-red-700">{it.valor_desconto > 0 ? fmtBRL(it.valor_desconto) : '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {it.memoria && (
                            <Button size="sm" variant="ghost" onClick={() => setMemExpandResc(memExpandResc === i ? null : i)}
                              title="Ver memória de cálculo" className="h-7 text-[10px] text-indigo-600">
                              <Eye className="h-3.5 w-3.5 mr-1" /> Memória
                            </Button>
                          )}
                          {it.origem === 'manual' && it.id && (
                            <Button size="sm" variant="ghost" onClick={() => editarEventoRescisao(it)}
                              title="Editar lançamento manual" className="h-7 text-[10px] text-slate-600">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {it.id && (
                            <Button size="sm" variant="ghost" onClick={() => removerEventoRescisao(it.id)}
                              title={it.origem === 'manual' ? 'Remover lançamento manual' : 'Excluir lançamento automático'} className="h-7 text-[10px] text-red-600">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {memExpandResc === i && it.memoria && (
                        <tr key={`mem-${i}`} className="bg-slate-50/50">
                          <td colSpan={5} className="p-3 pl-8 border-t border-slate-200">
                            <MemoriaLegivel memoria={it.memoria} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-bold">
                  <tr><td colSpan={3} className="p-2 text-right">LÍQUIDO →</td><td className="p-2 text-right text-xl text-indigo-700">{fmtBRL(detalhe.total_liquido)}</td><td /></tr>
                </tfoot>
              </table>

              {/* Memória de cálculo */}
              {detalhe.itens.some(it => it.memoria) && (
                <details className="text-xs mt-3">
                  <summary className="cursor-pointer font-semibold text-slate-700">📊 Memória de Cálculo (auditoria)</summary>
                  <div className="mt-2 space-y-2">
                    {detalhe.itens.filter(it => it.memoria).map((it, i) => (
                      <Card key={i} className="bg-slate-50">
                        <CardHeader className="pb-1"><CardTitle className="text-xs">{it.rubrica_codigo} — {it.rubrica_descricao}</CardTitle></CardHeader>
                        <CardContent>
                          <MemoriaLegivel memoria={it.memoria} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialogRescisao />
    </div>
  );
};

// ============================================================================
// EXPORT — Componente unificado com 3 sub-abas
// ============================================================================
const Decimo13FeriasRescisaoTab = () => {
  const [sub, setSub] = useState('decimo13');
  return (
    <Tabs value={sub} onValueChange={setSub}>
      <TabsList className="grid grid-cols-3 mb-4">
        <TabsTrigger value="decimo13" data-testid="dp-sub-13"><Gift className="h-4 w-4 mr-1" />13º Salário</TabsTrigger>
        <TabsTrigger value="ferias" data-testid="dp-sub-ferias"><Sun className="h-4 w-4 mr-1" />Férias</TabsTrigger>
        <TabsTrigger value="rescisao" data-testid="dp-sub-rescisao"><FileX className="h-4 w-4 mr-1" />Rescisão (TRCT)</TabsTrigger>
      </TabsList>
      <TabsContent value="decimo13"><Decimo13Tab /></TabsContent>
      <TabsContent value="ferias"><FeriasTab /></TabsContent>
      <TabsContent value="rescisao"><RescisaoTab /></TabsContent>
    </Tabs>
  );
};

export default Decimo13FeriasRescisaoTab;
